"use client";

import { Fragment, useEffect, useRef, useState } from "react";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/react";
import {
  ChevronDownIcon,
  DocumentArrowDownIcon,
} from "@heroicons/react/24/outline";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileExcel,
  faFilePdf,
  faFileZipper,
  faFolderOpen,
} from "@fortawesome/free-regular-svg-icons";
import DownloadNotifications from "../Notifications/DownloadNotifications";

// Catalog of every menu entry the export dropdown can render. Callers pick a
// subset via the `options` prop; entries are emitted in `options` order and a
// divider is drawn whenever the entry's `group` changes, so option ordering
// drives the visual grouping in the dropdown.
export const EXPORT_OPTION_IDS = {
  // Top group — full bundle.
  ZIP_ALL: "ZIP_ALL",
  // Excel group.
  EXCEL_RESPONSES: "EXCEL_RESPONSES",
  EXCEL_RISK_AUGMENTED: "EXCEL_RISK_AUGMENTED",
  EXCEL_PARTIAL: "EXCEL_PARTIAL",
  EXCEL_FULL: "EXCEL_FULL",
  // PDF group.
  PDF_DOCS: "PDF_DOCS",
  PDF_RISK_AUGMENTED: "PDF_RISK_AUGMENTED",
  PDF_PARTIAL: "PDF_PARTIAL",
  PDF_FULL: "PDF_FULL",
  // Bottom group — docs only.
  SUPPORTING_DOCS: "SUPPORTING_DOCS",
};

// Default = the six-item layout shown in the export-button design. Risk
// Augmented entries are appended via computeRfiExportOptions when the caller
// signals risk visibility, so this default works on its own too.
const DEFAULT_OPTIONS = [
  EXPORT_OPTION_IDS.ZIP_ALL,
  EXPORT_OPTION_IDS.EXCEL_RESPONSES,
  EXPORT_OPTION_IDS.EXCEL_RISK_AUGMENTED,
  EXPORT_OPTION_IDS.PDF_DOCS,
  EXPORT_OPTION_IDS.PDF_RISK_AUGMENTED,
  EXPORT_OPTION_IDS.SUPPORTING_DOCS,
];

export default function ExportMenu({
  exportExcel,
  exportDocs,
  exportPartial,
  exportRiskAugmented,
  // Bundle ZIP (responses + supporting documents). Defaults to the existing
  // `exportDocs("excel")` path so legacy callers don't need to update — the
  // alldocuments subtype already produces a ZIP on the worker side.
  exportZipAll,
  // Supporting documents only. New handler — caller must pass it; the menu
  // hides the SUPPORTING_DOCS entry when no handler is supplied so the option
  // isn't rendered without a working target.
  exportSupportingDocs,
  notification,
  setNotification,
  fileType,
  details,
  downloadFileName,
  exportLogsId,
  options = DEFAULT_OPTIONS,
}) {
  const [isExporting, setIsExporting] = useState(false);
  const exportTimeoutRef = useRef(null);

  // Clear the spinner the moment DownloadNotifications takes over.
  useEffect(() => {
    if (notification && isExporting) {
      setIsExporting(false);
      if (exportTimeoutRef.current) {
        clearTimeout(exportTimeoutRef.current);
        exportTimeoutRef.current = null;
      }
    }
  }, [notification, isExporting]);

  useEffect(() => () => {
    if (exportTimeoutRef.current) clearTimeout(exportTimeoutRef.current);
  }, []);

  const runExport = (fn) => {
    if (isExporting) return;
    setIsExporting(true);
    // Hard cap so a silent BE failure can't leave the button stuck mid-spin.
    exportTimeoutRef.current = setTimeout(() => setIsExporting(false), 30000);
    try {
      fn();
    } catch {
      setIsExporting(false);
    }
  };

  // Icons used by the menu entries — kept aligned with the existing color
  // language (Excel green, PDF red, Risk-augmented indigo, Zip rose, Docs slate).
  const ExcelIcon = ({ className = "text-green-700" }) => (
    <FontAwesomeIcon icon={faFileExcel} className={`w-[16px] h-[16px] ${className}`} />
  );
  const PdfIcon = ({ className = "text-red-700" }) => (
    <FontAwesomeIcon icon={faFilePdf} className={`w-[16px] h-[16px] ${className}`} />
  );
  const ZipIcon = ({ className = "text-rose-700" }) => (
    <FontAwesomeIcon icon={faFileZipper} className={`w-[16px] h-[16px] ${className}`} />
  );
  const DocsIcon = ({ className = "text-slate-600" }) => (
    <FontAwesomeIcon icon={faFolderOpen} className={`w-[16px] h-[16px] ${className}`} />
  );

  // Catalog of every renderable entry. `group` drives the visual separator
  // between groups; `method` is the click handler. Entries whose handler the
  // caller didn't provide are filtered out below.
  const ALL_ENTRIES = {
    [EXPORT_OPTION_IDS.ZIP_ALL]: {
      id: "ZIP_ALL",
      group: "zip",
      name: "Download All - ZIP",
      icon: <ZipIcon />,
      enabled: () => Boolean(exportZipAll || exportDocs),
      method: () => runExport(() => {
        // Prefer the dedicated handler when present, fall back to the existing
        // alldocuments-bundle path so existing call sites keep working.
        if (exportZipAll) return exportZipAll("excel");
        if (exportDocs) return exportDocs("excel");
      }),
    },
    [EXPORT_OPTION_IDS.EXCEL_RESPONSES]: {
      id: "EXCEL_RESPONSES",
      group: "excel",
      name: "Responses - Excel",
      icon: <ExcelIcon />,
      enabled: () => Boolean(exportExcel),
      method: () => runExport(() => exportExcel && exportExcel("excel")),
    },
    [EXPORT_OPTION_IDS.EXCEL_RISK_AUGMENTED]: {
      id: "EXCEL_RISK_AUGMENTED",
      group: "excel",
      name: "Responses + Risk Analysis - Excel",
      icon: <ExcelIcon className="text-indigo-700" />,
      enabled: () => Boolean(exportRiskAugmented),
      method: () => runExport(() => exportRiskAugmented && exportRiskAugmented("excel")),
    },
    [EXPORT_OPTION_IDS.EXCEL_PARTIAL]: {
      id: "EXCEL_PARTIAL",
      group: "excel",
      name: "Excel - Partially complete",
      icon: <ExcelIcon />,
      enabled: () => Boolean(exportPartial),
      method: () => runExport(() => exportPartial && exportPartial("excel")),
    },
    [EXPORT_OPTION_IDS.EXCEL_FULL]: {
      id: "EXCEL_FULL",
      group: "excel",
      name: "Responses + Documents - Excel",
      icon: <ZipIcon />,
      enabled: () => Boolean(exportDocs),
      method: () => runExport(() => exportDocs && exportDocs("excel")),
    },
    [EXPORT_OPTION_IDS.PDF_DOCS]: {
      id: "PDF_DOCS",
      group: "pdf",
      name: "Responses - PDF",
      icon: <PdfIcon />,
      enabled: () => Boolean(exportExcel),
      method: () => runExport(() => exportExcel && exportExcel("pdf")),
    },
    [EXPORT_OPTION_IDS.PDF_RISK_AUGMENTED]: {
      id: "PDF_RISK_AUGMENTED",
      group: "pdf",
      name: "Responses + Risk Analysis - PDF",
      icon: <PdfIcon className="text-indigo-700" />,
      enabled: () => Boolean(exportRiskAugmented),
      method: () => runExport(() => exportRiskAugmented && exportRiskAugmented("pdf")),
    },
    [EXPORT_OPTION_IDS.PDF_PARTIAL]: {
      id: "PDF_PARTIAL",
      group: "pdf",
      name: "PDF - Partially complete",
      icon: <PdfIcon />,
      enabled: () => Boolean(exportPartial),
      method: () => runExport(() => exportPartial && exportPartial("pdf")),
    },
    [EXPORT_OPTION_IDS.PDF_FULL]: {
      id: "PDF_FULL",
      group: "pdf",
      name: "Responses + Documents - PDF",
      icon: <ZipIcon />,
      enabled: () => Boolean(exportDocs),
      method: () => runExport(() => exportDocs && exportDocs("pdf")),
    },
    [EXPORT_OPTION_IDS.SUPPORTING_DOCS]: {
      id: "SUPPORTING_DOCS",
      group: "docs",
      name: "Supporting Documents",
      icon: <DocsIcon />,
      enabled: () => Boolean(exportSupportingDocs),
      method: () => runExport(() => exportSupportingDocs && exportSupportingDocs()),
    },
  };

  const entries = options
    .map((id) => ALL_ENTRIES[id])
    .filter((entry) => entry && entry.enabled());

  return (
    <Menu as="div" className="relative inline-block text-left">
      <MenuButton
        disabled={isExporting}
        className={`inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-900 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 ${
          isExporting ? "cursor-wait opacity-80" : ""
        }`}
      >
        {isExporting ? (
          <>
            <ArrowPathIcon className="h-4 w-4 animate-spin text-indigo-600" />
            <span>Preparing…</span>
          </>
        ) : (
          <>
            <DocumentArrowDownIcon className="h-4 w-4" aria-hidden="true" />
            <span>Export</span>
            <ChevronDownIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
          </>
        )}
      </MenuButton>

      <MenuItems
        transition
        anchor="bottom end"
        className="z-20 mt-2 w-72 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75"
      >
        {entries.map((entry, idx) => {
          const prev = entries[idx - 1];
          const needsDivider = prev && prev.group !== entry.group;
          return (
            <Fragment key={entry.id}>
              {needsDivider ? (
                <div className="my-1 border-t border-gray-100" aria-hidden="true" />
              ) : null}
              <MenuItem>
                {({ focus }) => (
                  <button
                    type="button"
                    onClick={entry.method}
                    className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-900 ${
                      focus ? "bg-gray-50" : ""
                    }`}
                  >
                    <span className="shrink-0">{entry.icon}</span>
                    <span className="truncate">{entry.name}</span>
                  </button>
                )}
              </MenuItem>
            </Fragment>
          );
        })}
      </MenuItems>

      <DownloadNotifications
        show={notification}
        setShow={setNotification}
        fileType={fileType}
        id={details?.questionnaire_id}
        name={details?.name}
        downloadFileName={downloadFileName}
        exportLogsId={exportLogsId}
      />
    </Menu>
  );
}
